<template>
    <div class="bmv-dd" :class="{ 'bmv-dd--open': isOpen, 'bmv-dd--stretch': stretch }">
        <div ref="trigger" class="bmv-dd-trigger" @click="onTriggerClick">
            <slot name="trigger" :open="isOpen" />
        </div>

        <!--
            Le panneau part dans un portail : la ligne d'un tableau vit dans un
            conteneur qui rogne (`.bmv-table-wrap` est en `overflow: hidden`,
            `.bmv-table-container` en `overflow-x: auto`), et un menu en
            `position: absolute` y disparaît — de façon spectaculaire quand il
            n'y a qu'une ligne, puisque le conteneur s'arrête juste sous elle.
        -->
        <portal v-if="isOpen" :to="portalTo">
            <div
                ref="panel"
                class="bmv-dd-menu"
                :style="panelStyle"
                @click="onPanelClick"
            >
                <slot :close="close" />
            </div>
        </portal>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
export default {
    name: 'BmvDropdown',
    props: {
        // 'right' aligne le menu sur le bord droit du déclencheur.
        align: { type: String, default: 'left' },
        minWidth: { type: Number, default: 190 },
        maxHeight: { type: Number, default: 320 },
        disabled: { type: Boolean, default: false },
        // Le déclencheur occupe toute la largeur disponible : c'est le cas des
        // boutons pleine cellule (classes `w-full` / `h-full`), pas des
        // pastilles qui se dimensionnent sur leur texte.
        stretch: { type: Boolean, default: false },
        // Un clic sur un élément du menu le referme : c'est ce qu'on veut pour
        // une liste de choix, pas pour un panneau de filtres.
        closeOnSelect: { type: Boolean, default: true },
        portalTo: { type: String, default: 'dropdown-portal' }
    },
    data() {
        return {
            isOpen: false,
            panelStyle: {}
        }
    },
    watch: {
        isOpen(open) {
            if (open) {
                this.updatePosition()
                // Deuxième passe une fois le panneau mesurable : c'est sa
                // hauteur réelle qui décide s'il s'ouvre vers le haut.
                this.$nextTick(this.updatePosition)
                document.addEventListener('mousedown', this.onOutsideClick)
                document.addEventListener('keydown', this.onKeydown)
                window.addEventListener('scroll', this.updatePosition, true)
                window.addEventListener('resize', this.updatePosition)
            } else {
                this.unbind()
            }
            this.$emit(open ? 'open' : 'close')
        }
    },
    beforeDestroy() {
        this.unbind()
    },
    methods: {
        unbind() {
            document.removeEventListener('mousedown', this.onOutsideClick)
            document.removeEventListener('keydown', this.onKeydown)
            window.removeEventListener('scroll', this.updatePosition, true)
            window.removeEventListener('resize', this.updatePosition)
        },
        onTriggerClick(event) {
            if (this.disabled) return
            // La ligne du tableau est cliquable : le menu ne doit pas ouvrir la
            // fiche du ticket au passage.
            event.preventDefault()
            event.stopPropagation()
            this.isOpen = !this.isOpen
        },
        onPanelClick(event) {
            event.stopPropagation()
            if (this.closeOnSelect) this.close()
        },
        close() {
            this.isOpen = false
        },
        onKeydown(event) {
            if (event.key === 'Escape') this.close()
        },
        onOutsideClick(event) {
            const inTrigger = this.$el && this.$el.contains(event.target)
            const inPanel = this.$refs.panel && this.$refs.panel.contains(event.target)
            if (!inTrigger && !inPanel) this.close()
        },
        updatePosition() {
            if (!this.isOpen || !this.$refs.trigger) return
            const rect = this.$refs.trigger.getBoundingClientRect()
            const vw = window.innerWidth
            const vh = window.innerHeight
            const gap = 6
            const edge = 8
            const panelHeight = (this.$refs.panel && this.$refs.panel.offsetHeight) || this.maxHeight
            const spaceBelow = vh - rect.bottom - gap - edge
            const spaceAbove = rect.top - gap - edge
            const flip = spaceBelow < Math.min(panelHeight, this.maxHeight) && spaceAbove > spaceBelow

            const width = Math.max(rect.width, this.minWidth)
            let left = this.align === 'right' ? rect.right - width : rect.left
            if (left + width > vw - edge) left = vw - width - edge
            if (left < edge) left = edge

            const style = {
                position: 'fixed',
                left: left + 'px',
                width: width + 'px',
                maxHeight: Math.max(140, Math.min(this.maxHeight, flip ? spaceAbove : spaceBelow)) + 'px',
                zIndex: 10000
            }
            if (flip) {
                style.bottom = (vh - rect.top + gap) + 'px'
            } else {
                style.top = (rect.bottom + gap) + 'px'
            }
            this.panelStyle = style
        }
    }
}
</script>

<style scoped>
.bmv-dd {
    display: inline-flex;
    max-width: 100%;
}

.bmv-dd-trigger {
    display: inline-flex;
    max-width: 100%;
}

.bmv-dd--stretch,
.bmv-dd--stretch .bmv-dd-trigger {
    display: flex;
    width: 100%;
    height: 100%;
}

.bmv-dd-menu {
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
    font-size: 0.84rem;
    color: #334155;
}

@media (max-width: 640px) {
    .bmv-dd-menu {
        border-radius: 12px;
        box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
    }
}
</style>
